import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Check, Calendar, Users, Sparkles, Zap, Clock, DollarSign, ArrowRight, Package, Activity, Shield, FileText, Code, Key, Fingerprint, Lock, TrendingUp, Star, Link2, QrCode, Camera, Bell, Download, Mail, Menu, X, Globe, Bitcoin, BadgeDollarSign, Percent, SlidersHorizontal, BarChart3, Anchor, MapPin, Database, ArrowLeftRight, Megaphone, MousePointerClick, Compass, MessageCircle, LifeBuoy, CloudSun, Waves, Wind, Moon, RefreshCw, FileSpreadsheet, Handshake, ChevronDown } from 'lucide-react';
import { BeaconIcon } from '../components/BeaconIcon';
import { QRCodeSVG as QRCodeComponent } from 'qrcode.react';
import { getAllPlans, formatPrice } from '../config/pricing';
import { SocialLinksDark } from '../components/SocialLinks';
import HelpScoutBeacon from '../components/HelpScoutBeacon';
import WhatsAppFloat from '../components/WhatsAppFloat';
import { FloatingNavbar } from '../components/landing/FloatingNavbar';
import { LogosBlurFlip } from '../components/landing/LogosBlurFlip';
import { NoiseBackground } from '../components/ui/noise-background';
import { NoiseStripsHero } from '../components/ui/noise-strips-hero';
import { StatsSection } from '../components/landing/StatsSection';
import { GlowingCardsSection } from '../components/landing/GlowingCardsSection';
import { EmbedTerminal } from '../components/landing/EmbedTerminal';
import { SparklesCore } from '../components/ui/sparkles';
import { StickyScroll } from '../components/ui/sticky-scroll-reveal';
import { TracingBeam } from '../components/ui/tracing-beam';
import { WorldMap } from '../components/ui/world-map';
import { BackgroundBeamsWithCollision } from '../components/ui/background-beams-with-collision';
import { FaqSection } from '../components/landing/FaqSection';
import { getInitialLang, setStoredLang } from '../utils/uaeLocale';
import { initMetaPixel } from '../utils/analytics';
import { KioskDirectSection } from '../components/KioskDirectSection';
import { proxyImg } from '../utils/img';
import { EvervaultCard, Icon as EvervaultIcon } from '../components/ui/evervault-card';

// Auto-rotating top announcement banner slides (module scope = stable identity).
const ANNOUNCEMENTS = [
  {
    href: '/blog/welcoming-trail-blazer-yachts',
    bg: 'bg-gradient-to-r from-blue-600 via-blue-500 to-indigo-600 hover:from-blue-700 hover:via-blue-600 hover:to-indigo-700',
    text: 'text-white',
    bold: 'Welcoming Trail Blazer Yachts',
    rest: '\u00A0— Dubai luxury fleet, incl. the Lamborghini 63, now on BookingFlows',
    cta: 'read the story',
  },
  {
    href: '/customers/boatx',
    bg: 'bg-gradient-to-r from-blue-600 via-blue-500 to-indigo-600 hover:from-blue-700 hover:via-blue-600 hover:to-indigo-700',
    text: 'text-white',
    bold: 'BOATX processed $100k+ in bookings',
    rest: '\u00A0through BookingFlows',
    cta: '— see how',
  },
  {
    href: '/customers/banana-boat-rentals',
    bg: 'bg-gradient-to-r from-blue-600 via-blue-500 to-indigo-600 hover:from-blue-700 hover:via-blue-600 hover:to-indigo-700',
    text: 'text-white',
    bold: 'Welcoming Banana Boat Rentals',
    rest: '\u00A0— 15+ boat fleet now managed on BookingFlows',
    cta: 'read their story',
  },
  {
    href: '/meet-the-team',
    bg: 'bg-gradient-to-r from-blue-600 via-blue-500 to-indigo-600 hover:from-blue-700 hover:via-blue-600 hover:to-indigo-700',
    text: 'text-white',
    bold: 'USCG · RYA · MCA · IYT · ASA',
    rest: '\u00A0— built by certified captains',
    cta: 'meet the team',
  },
];


const SupplierLandingPage = () => {
  const navigate = useNavigate();
  const plans = getAllPlans(false); // Use demo pricing
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [authMenuOpen, setAuthMenuOpen] = useState(false);
  const [lang, setLang] = useState(getInitialLang);
  const isAr = lang === 'ar';
  const switchLang = (l) => { setLang(l); setStoredLang(l); };
  // Meta Pixel on the root landing page (PageView)
  useEffect(() => { initMetaPixel(); }, []);
  // Operator spotlight carousel — FBWS (international) first, then BBR
  const [spotSlide, setSpotSlide] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setSpotSlide((s) => (s + 1) % 4), 9000);
    return () => clearInterval(t);
  }, []);
  // Top announcement banner — slides up after the user scrolls past the hero
  const [bannerVisible, setBannerVisible] = useState(true);
  // Floating "Start free" CTA — only appears once the user is past the hero,
  // so it doesn't compete with the in-hero buttons. Pure conversion nudge for
  // visitors who scroll deep without ever returning to the nav.
  const [showFloatingCta, setShowFloatingCta] = useState(false);

  // Typed rotator for the gradient hero buzz-line. Cycles through a small set
  // of operator-resonant outcomes (Less busywork → More time for what matters
  // → Happier customers → …). Pure CSS caret + JS typing/erasing — no deps.
  // Honors prefers-reduced-motion: users who opt out see the first phrase
  // rendered statically with no animation.
  const BUZZ_LINES = [
    'Less busywork.',
    'More time.',
    'Happy customers.',
    'Higher margins.',
    'Fewer no-shows.',
  ];
  const [buzzIdx, setBuzzIdx] = useState(0);
  const [buzzText, setBuzzText] = useState(BUZZ_LINES[0]);
  const [buzzPhase, setBuzzPhase] = useState('hold'); // hold → erase → type → hold
  useEffect(() => {
    const prefersReduced = typeof window !== 'undefined' &&
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return; // Leave the static first phrase visible.

    const fullText = BUZZ_LINES[buzzIdx];
    let timer;
    if (buzzPhase === 'hold') {
      timer = setTimeout(() => setBuzzPhase('erase'), 1800);
    } else if (buzzPhase === 'erase') {
      if (buzzText.length === 0) {
        const next = (buzzIdx + 1) % BUZZ_LINES.length;
        setBuzzIdx(next);
        setBuzzPhase('type');
      } else {
        timer = setTimeout(() => setBuzzText(buzzText.slice(0, -1)), 35);
      }
    } else if (buzzPhase === 'type') {
      if (buzzText.length === fullText.length) {
        setBuzzPhase('hold');
      } else {
        timer = setTimeout(() => setBuzzText(fullText.slice(0, buzzText.length + 1)), 65);
      }
    }
    return () => clearTimeout(timer);
  }, [buzzPhase, buzzText, buzzIdx]);

  // Auto-rotating announcement banner index (crossfade). Pauses on hover;
  // honors prefers-reduced-motion (no auto-advance).
  const [announceIdx, setAnnounceIdx] = useState(0);
  const [announcePaused, setAnnouncePaused] = useState(false);
  useEffect(() => {
    const prefersReduced = typeof window !== 'undefined' &&
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced || announcePaused) return;
    const id = setInterval(() => {
      setAnnounceIdx((i) => (i + 1) % ANNOUNCEMENTS.length);
    }, 6000);
    return () => clearInterval(id);
  }, [announcePaused]);

  // Lightweight wrapper around analytics util (kept inside component so the
  // import isn't loaded on first paint).
  const trackSignupStarted = (source) => {
    import('../utils/analytics').then((m) => {
      if (typeof m.trackSignupStarted === 'function') {
        m.trackSignupStarted(source);
      }
    }).catch(() => {});
  };

  // Add scroll animation observer + banner auto-hide on scroll
  useEffect(() => {
    // Fire supplier page view — B2B lead signal for Google Ads
    import('../utils/analytics').then(({ trackSupplierPageView }) => {
      trackSupplierPageView('/for-suppliers');
    });

    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in-up');
        }
      });
    }, observerOptions);

    // Observe all sections with the class 'animate-on-scroll'
    document.querySelectorAll('.animate-on-scroll').forEach((el) => {
      observer.observe(el);
    });

    // Hide top banner once user has scrolled past ~120px (past hero start).
    // Show the floating "Start free" CTA once the user is past ~700px (deep
    // enough that they've engaged with the page but no longer see the
    // primary hero CTAs).
    const onScroll = () => {
      const y = window.scrollY;
      setBannerVisible(y < 120);
      setShowFloatingCta(y > 700);
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  return (
    <div className={`min-h-screen bg-white flex flex-col ${isAr ? 'lang-ar' : ''}`}>
      {isAr ? <WhatsAppFloat /> : <HelpScoutBeacon />}
      <Helmet>
        <html lang={isAr ? 'ar' : 'en'} />
        <title>Online Booking Software for Tours, Charters &amp; Rentals | BookingFlows</title>
        <meta name="description" content="BookingFlows is online booking software for tours, charters, rentals &amp; experience businesses. Take bookings 24/7, get paid automatically, manage your team. Sign up free." />
        <meta property="og:title" content="Online Booking Software | BookingFlows" />
        <meta property="og:description" content="Take bookings 24/7, get paid automatically, manage your team. Free to start." />
        {/* Canonical points to the root — / and /for-suppliers serve the same
            page so this avoids duplicate-content penalties while keeping the
            /for-suppliers route alive for existing Google Ads campaigns. */}
        <link rel="canonical" href="https://bookingflows.com/" />
      </Helmet>
      <style>{`
        /* Smooth scroll animations - subtle and professional */
        .animate-on-scroll {
          opacity: 0;
          transform: translateY(16px);
          transition: opacity 0.6s ease-out, transform 0.6s ease-out;
        }
        
        .animate-fade-in-up {
          opacity: 1;
          transform: translateY(0);
        }

        /* Smooth scroll behavior */
        html {
          scroll-behavior: smooth;
        }
        
        /* Enhanced button hover states */
        .btn-supplier-primary {
          transition: all 0.2s ease;
        }
        .btn-supplier-primary:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(37, 99, 235, 0.25);
        }
        .btn-supplier-primary:active {
          transform: translateY(0);
        }
      `}</style>
      
      {/* Top announcement banner — auto-rotating crossfade (BOATX blue ⇄ Banana
          Boat Rentals yellow ⇄ credential ribbon). Fixed height (h-11) so every
          slide stays vertically centered on all screens. Pauses on hover;
          auto-hides on scroll past the hero. */}
      <div
        data-testid="supplier-top-banner"
        className={`relative overflow-hidden transition-[max-height,opacity] duration-300 ease-out ${
          bannerVisible ? 'h-11 max-h-12 opacity-100' : 'h-11 max-h-0 opacity-0'
        }`}
        onMouseEnter={() => setAnnouncePaused(true)}
        onMouseLeave={() => setAnnouncePaused(false)}
      >
        {ANNOUNCEMENTS.map((a, i) => (
          <a
            key={i}
            href={a.href}
            data-testid={`top-banner-slide-${i}`}
            aria-hidden={i !== announceIdx}
            className={`absolute inset-0 flex items-center justify-center text-center text-xs sm:text-sm font-medium tracking-wide leading-none transition-opacity duration-700 ease-out ${a.bg} ${a.text} ${
              i === announceIdx ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
          >
            <div className="w-full max-w-7xl mx-auto px-3 sm:px-4 flex items-center justify-center gap-1.5 sm:gap-2">
              <span>
                <span className="font-semibold">{a.bold}</span>
                <span className="hidden sm:inline">{a.rest}</span>
              </span>
              <span className="opacity-90 underline-offset-2 hover:underline whitespace-nowrap inline-flex items-center gap-1 shrink-0">
                {a.cta}
                <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </div>
          </a>
        ))}
      </div>

      <FloatingNavbar isAr={isAr} switchLang={switchLang} />

      {/* Hero Section — Aceternity noise/strips background: accordion gradient
          texture behind the headline. Kept tight so the trusted-infra strip
          is clearly visible on a laptop fold. */}
      <section className="relative bg-white -mt-[60px]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-[72px] sm:pt-20">
          <NoiseStripsHero className="rounded-3xl" data-testid="supplier-hero-frame">
            <div className="relative z-20 text-center max-w-4xl mx-auto px-4 sm:px-8" dir={isAr ? 'rtl' : undefined}>
          {/* Eyebrow — soft blue pill in the lamp glow */}
          <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 sm:px-4 py-1.5 sm:py-2 text-blue-600 text-[10px] sm:text-xs font-semibold uppercase tracking-[0.14em] sm:tracking-[0.2em] whitespace-nowrap mb-6">
            <Sparkles className="w-3.5 h-3.5 shrink-0" />
            {isAr ? 'برنامج الحجوزات للمشغّلين' : 'Booking Software for Operators'}
          </div>

          {/* Headline — dark first line, brand-blue typed rotator second. */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-gray-900 leading-[1.05] mb-5">
            {isAr ? 'حجوزات أكثر.' : 'More bookings.'}<br />
            <span
              className="text-blue-600 inline-block leading-[1.18] pb-1 whitespace-nowrap"
              aria-live="polite"
              aria-atomic="true"
              data-testid="supplier-hero-rotator"
            >
              {isAr ? 'إدارة أسهل. نموّ أسرع.' : (buzzText || '\u00A0')}
            </span>
            {!isAr && (
              <span
                className="inline-block w-[0.08em] h-[0.85em] align-[-0.08em] ml-1 bg-blue-600 animate-pulse"
                aria-hidden="true"
              />
            )}
          </h1>

          <p className="text-base sm:text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto mb-9 px-2">
            {isAr
              ? 'منصة الحجز الشاملة التي بناها مشغّلو الجولات والرحلات البحرية والتأجير — لأجل المشغّلين. استقبل الحجوزات على مدار الساعة، واستلم أموالك فوراً، وأدر فريقك بسهولة.'
              : <>The all-in-one booking platform built by tour, charter &amp; rental operators —
            for operators. Take bookings 24/7, get paid instantly, run your team.</>}
          </p>

          <div className="flex flex-row flex-wrap gap-2.5 sm:gap-3 justify-center items-center">
            <NoiseBackground
              containerClassName="w-fit p-[5px] rounded-full"
              gradientColors={["rgb(37, 99, 235)", "rgb(125, 211, 252)", "rgb(29, 78, 216)"]}
            >
              <button
                onClick={() => { trackSignupStarted('supplier_hero'); navigate('/signup'); }}
                className="inline-flex items-center h-full w-full cursor-pointer rounded-full bg-blue-600 hover:bg-blue-500 px-5 sm:px-8 py-3 sm:py-3.5 text-[15px] sm:text-base font-medium text-white shadow-[0px_2px_0px_0px_rgba(255,255,255,0.22)_inset] transition-[background-color,transform] duration-150 active:scale-[0.98]"
                data-testid="supplier-hero-signup"
              >
                {isAr ? 'ابدأ مجاناً' : 'Start free'} <ArrowRight className={isAr ? 'mr-2 rotate-180 w-4 h-4' : 'ml-2 w-4 h-4'} />
              </button>
            </NoiseBackground>
            <button
              onClick={() => navigate('/meet-the-team')}
              className="text-[15px] sm:text-base font-medium px-4 sm:px-7 py-3 sm:py-3.5 rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
              data-testid="supplier-hero-meet-team"
            >
              {isAr ? 'تعرّف على الفريق' : 'Meet the team'}
            </button>
          </div>

          <p className="text-[11px] sm:text-xs text-gray-400 mt-5 tracking-wide">
            {isAr ? 'إعداد مجاني · لا حاجة لبطاقة ائتمان · جاهز خلال 15 دقيقة' : 'Free setup · No credit card · Live in 15 minutes'}
          </p>
            </div>
          </NoiseStripsHero>
        </div>
      </section>

      {/* Trusted-by — rotating blur-flip logo sets (Aceternity), our real infra. */}
      <LogosBlurFlip isAr={isAr} />


      {/* Customer Story bar removed — case study lives at /customers/boatx
          and is reachable via the "See the BOATX story" hero CTA. Keeps
          the homepage breathing into the feature grid below. */}


      {/* ───────────────────────────────────────────────────────────────
          Operator spotlight carousel — Fun Beach Water Sports (Dubai, UAE;
          the "gone international" flex) leads, then crossfades to Banana
          Boat Rentals (Austin, TX). Auto-advances every 9s; dots to switch.
      ─────────────────────────────────────────────────────────────── */}
      <section className="py-24 bg-white" data-testid="supplier-spotlight-carousel">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid">
            <div
              className={`col-start-1 row-start-1 transition-opacity duration-700 ${spotSlide === 0 ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
              aria-hidden={spotSlide !== 0}
              data-testid="spotlight-slide-tb"
            >
            <div className="relative rounded-[2rem] overflow-hidden bg-gradient-to-br from-[#050b18] via-[#0a1428] to-black text-white shadow-[0_40px_90px_-35px_rgba(0,0,0,0.65)]">
              <div className="absolute inset-0 opacity-[0.12] pointer-events-none"
                   style={{ backgroundImage: "radial-gradient(circle at 1px 1px, rgba(56,189,248,0.5) 1px, transparent 0)", backgroundSize: "30px 30px" }} />
              <div className="absolute -top-24 -right-16 w-96 h-96 rounded-full bg-sky-400/15 blur-3xl pointer-events-none" />

              <div className="relative grid lg:grid-cols-2 gap-12 lg:gap-16 items-center p-7 sm:p-10 lg:p-14">
                {/* Left — copy */}
                <div>
                  <div className="flex justify-center lg:justify-start mb-6">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sky-400/15 text-[11px] font-semibold uppercase tracking-widest text-sky-200" data-testid="tb-spotlight-badge">
                      <Anchor className="w-3.5 h-3.5 text-sky-300" /> Newest on BookingFlows
                    </div>
                  </div>

                  <h2 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold tracking-tight leading-[1.08] mb-4" dir={isAr ? 'rtl' : undefined}>
                    {isAr ? <>نرحّب بـ<span className="bg-gradient-to-r from-sky-300 to-cyan-400 bg-clip-text text-transparent">Trail Blazer Yachts</span> — دبي، الإمارات العربية المتحدة.</> : <>Welcoming <span className="bg-gradient-to-r from-sky-300 to-cyan-400 bg-clip-text text-transparent">Trail Blazer Yachts</span> — Dubai, United Arab Emirates.</>}
                  </h2>
                  <p className="text-base sm:text-lg text-gray-300 leading-relaxed mb-7 max-w-xl" dir={isAr ? 'rtl' : undefined}>
                    {isAr ? 'أسطول فاخر من 13 يختاً بقيادة يخت Tecnomar for Lamborghini 63 — إلى جانب الجت سكي والإي-فويل والرياضات المائية انطلاقاً من دبي مارينا. 19 تجربة تعمل الآن على BookingFlows بالدرهم عبر Ziina.' : <>A 13-yacht luxury fleet led by the Tecnomar for Lamborghini 63 — plus jet skis,
                    eFoils and watersports out of Dubai Marina. All 19 experiences now run on
                    BookingFlows, paid in dirhams on Ziina, the UAE's homegrown payment rail.</>}
                  </p>

                  <div className="flex flex-wrap gap-2.5 mb-6">
                    {[
                      { icon: Anchor, label: '13-yacht luxury fleet' },
                      { icon: Zap,    label: 'Lamborghini 63 flagship' },
                      { icon: Waves,  label: 'Jet ski · eFoil · watersports' },
                    ].map(({ icon: Icon, label }) => (
                      <span key={label} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 text-sm text-gray-200">
                        <Icon className="w-3.5 h-3.5 text-sky-300" /> {label}
                      </span>
                    ))}
                  </div>

                  <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mb-8" data-testid="tb-intl-chips">
                    {[
                      'AED payments on Ziina',
                      'Dubai Marina departures',
                      'Live on the UAE Marketplace',
                    ].map((t) => (
                      <span key={t} className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-400">
                        <Check className="w-3.5 h-3.5 text-sky-300" /> {t}
                      </span>
                    ))}
                  </div>

                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      onClick={() => navigate('/blog/welcoming-trail-blazer-yachts')}
                      data-testid="tb-spotlight-story-btn"
                      className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-sky-400 text-black font-semibold hover:bg-sky-300 transition-colors"
                    >
                      Read the story <ArrowRight className="w-4 h-4" />
                    </button>
                    <a
                      href="https://www.instagram.com/trailblazer_yachts_dxb"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="tb-spotlight-ig-btn"
                      className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white/10 hover:bg-white/15 text-white font-medium transition-colors backdrop-blur"
                    >
                      @trailblazer_yachts_dxb <ArrowRight className="w-4 h-4" />
                    </a>
                  </div>
                </div>

                {/* Right — TB photo collage: Lambo hero + Majesty 80 & Jet Ski */}
                <div className="relative" data-testid="tb-spotlight-collage">
                  <div className="relative rounded-3xl overflow-hidden shadow-2xl ring-1 ring-white/10">
                    <img
                      src={proxyImg("https://1iynnenkjdavmviy.public.blob.vercel-storage.com/b41c98a3-72d5-4e0f-9a67-38c1d24f56e9_real_lambo_63_hero.webp")}
                      alt="The Tecnomar for Lamborghini 63 — Trail Blazer Yachts, Dubai"
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute bottom-3 left-3 bg-white/95 backdrop-blur text-gray-900 rounded-xl shadow-xl px-3.5 py-2 flex items-center gap-2 max-w-[260px]">
                      <Zap className="w-4 h-4 text-sky-500 flex-shrink-0" />
                      <div className="text-xs leading-tight">
                        <div className="font-semibold">Tecnomar for Lamborghini 63</div>
                        <div className="text-gray-500">63 knots · Dubai Marina</div>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    {[
                      {
                        id: 'majesty-80',
                        src: 'https://1iynnenkjdavmviy.public.blob.vercel-storage.com/b41c98a3-72d5-4e0f-9a67-38c1d24f56e9_real_majesty_80.jpeg',
                        alt: 'Majesty 80 superyacht — Trail Blazer Yachts, Dubai',
                        label: 'Majesty 80',
                        sub: 'Superyacht charter',
                      },
                      {
                        id: 'jet-ski',
                        src: 'https://1iynnenkjdavmviy.public.blob.vercel-storage.com/b41c98a3-72d5-4e0f-9a67-38c1d24f56e9_real_jetski_card.jpeg',
                        alt: 'Jet ski tour off Dubai Marina — Trail Blazer Yachts',
                        label: 'Jet Ski Tour',
                        sub: 'Dubai Marina',
                      },
                    ].map(({ id, src, alt, label, sub }) => (
                      <div key={id} className="relative rounded-2xl overflow-hidden ring-1 ring-white/10 shadow-xl" data-testid={`tb-collage-${id}`}>
                        <img src={proxyImg(src)} alt={alt} className="w-full h-28 sm:h-36 object-cover" loading="lazy" />
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-3 pb-2 pt-6">
                          <div className="text-xs font-semibold text-white">{label}</div>
                          <div className="text-[10px] text-gray-300">{sub}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            </div>
            <div
              className={`col-start-1 row-start-1 transition-opacity duration-700 ${spotSlide === 1 ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
              aria-hidden={spotSlide !== 1}
              data-testid="spotlight-slide-fbws"
            >
            <div className="relative rounded-[2rem] overflow-hidden bg-gradient-to-br from-[#050d1f] via-[#0b1d3a] to-black text-white shadow-[0_40px_90px_-35px_rgba(0,0,0,0.65)]">
              {/* dot texture + gold glow */}
              <div className="absolute inset-0 opacity-[0.15] pointer-events-none"
                   style={{ backgroundImage: "radial-gradient(circle at 1px 1px, rgba(251,191,36,0.5) 1px, transparent 0)", backgroundSize: "30px 30px" }} />
              <div className="absolute -top-24 -left-16 w-96 h-96 rounded-full bg-amber-400/15 blur-3xl pointer-events-none" />

              <div className="relative grid lg:grid-cols-2 gap-12 lg:gap-16 items-center p-7 sm:p-10 lg:p-14">
                {/* Left — floating collage */}
                <div className="relative order-2 lg:order-1">
                  <div className="grid grid-cols-2 grid-rows-3 gap-3 aspect-[4/5]">
                    <img
                      src="/images/fbws-jetski.webp"
                      alt="A Fun Beach Water Sports jet ski in front of the Burj Al Arab, Dubai"
                      className="col-span-2 row-span-2 w-full h-full object-cover rounded-2xl shadow-xl ring-1 ring-white/10"
                      loading="lazy"
                      decoding="async"
                    />
                    <img
                      src="/images/fbws-jetcar.webp"
                      alt="The Fun Beach Water Sports jet car on the water off the Dubai coastline"
                      className="w-full h-full object-cover rounded-2xl shadow-xl ring-1 ring-white/10"
                      loading="lazy"
                      decoding="async"
                    />
                    <img
                      src="/images/fbws-flyboard.webp"
                      alt="A flyboard session with Fun Beach Water Sports in Dubai"
                      className="w-full h-full object-cover rounded-2xl shadow-xl ring-1 ring-white/10"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>

                  {/* floating Fun Beach logo — top-left */}
                  <div className="absolute -top-3 -left-2 sm:-left-4 z-10 bg-white rounded-xl ring-1 ring-black/10 px-2.5 py-1.5 shadow-2xl">
                    <img src="/images/fbws-logo.webp"
                         alt="Fun Beach Water Sports" className="h-8 sm:h-10 w-auto object-contain" loading="lazy" />
                  </div>
                  {/* floating location pill */}
                  <div className="absolute -top-3 -right-2 sm:-right-4 bg-amber-400 text-black rounded-xl px-3.5 py-2 font-bold text-sm shadow-2xl flex items-center gap-1.5">
                    <MapPin className="w-4 h-4" /> Dubai · UAE
                  </div>
                  {/* floating international pill */}
                  <div className="absolute -bottom-4 -right-2 sm:-right-5 bg-white text-gray-900 rounded-2xl shadow-2xl px-4 py-3 flex items-center gap-2.5 max-w-[260px]">
                    <Globe className="w-6 h-6 text-[#0b1d3a] shrink-0" />
                    <div className="text-xs leading-tight">
                      <div className="font-semibold">Two continents, one platform</div>
                      <div className="text-gray-500">AED processed · GST (UTC+4)</div>
                    </div>
                  </div>
                  {/* floating review pill */}
                  <div className="absolute -bottom-4 -left-2 sm:-left-5 bg-white text-gray-900 rounded-2xl shadow-2xl px-4 py-3 flex items-center gap-2.5 max-w-[260px]">
                    <div className="flex">
                      {[0,1,2,3,4].map(i => <Star key={i} className="w-3 h-3 fill-amber-400 text-amber-400" />)}
                    </div>
                    <div className="text-xs leading-tight">
                      <div className="font-semibold">1,059 Google reviews</div>
                      <div className="text-gray-500">5.0 · Dubai, UAE</div>
                    </div>
                  </div>
                </div>

                {/* Right — copy */}
                <div className="order-1 lg:order-2">
                  <div className="flex justify-center lg:justify-start mb-6">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-400/15 text-[11px] font-semibold uppercase tracking-widest text-amber-200" data-testid="fbws-international-badge">
                      <Globe className="w-3.5 h-3.5 text-amber-300" /> BookingFlows goes international
                    </div>
                  </div>

                  <h2 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold tracking-tight leading-[1.08] mb-4" dir={isAr ? 'rtl' : undefined}>
                    {isAr ? <>نرحّب بـ<span className="bg-gradient-to-r from-amber-300 to-yellow-400 bg-clip-text text-transparent">Fun Beach Water Sports</span> — دبي، الإمارات العربية المتحدة.</> : <>Welcoming <span className="bg-gradient-to-r from-amber-300 to-yellow-400 bg-clip-text text-transparent">Fun Beach Water Sports</span> — Dubai, United Arab Emirates.</>}
                  </h2>
                  <p className="text-base sm:text-lg text-gray-300 leading-relaxed mb-7 max-w-xl" dir={isAr ? 'rtl' : undefined}>
                    {isAr ? 'أول مشغّل لنا على الجانب الآخر من الكوكب. يدير Fun Beach أسطولاً كاملاً للرياضات المائية على ساحل دبي — جت سكي، وجت كار، وفلاي بورد — والعملية بأكملها تعمل الآن على BookingFlows: حجوزات بالدرهم، وجداول بتوقيت الخليج، ومنصة واحدة.' : <>Our first operator on the other side of the planet. Fun Beach runs a full
                    watersports fleet on the Dubai coastline — jet skis, the jet car, flyboards —
                    and the whole operation now runs on BookingFlows: bookings in dirhams,
                    schedules on Gulf Standard Time, same platform.</>}
                  </p>

                  <div className="flex flex-wrap gap-2.5 mb-6">
                    {[
                      { icon: Waves, label: 'Jet ski fleet' },
                      { icon: Zap,   label: 'Jet car & flyboard' },
                      { icon: Star,  label: '1,059 five-star reviews' },
                    ].map(({ icon: Icon, label }) => (
                      <span key={label} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 text-sm text-gray-200">
                        <Icon className="w-3.5 h-3.5 text-amber-300" /> {label}
                      </span>
                    ))}
                  </div>

                  <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mb-8" data-testid="fbws-intl-chips">
                    {[
                      'AED payments, natively processed',
                      'Gulf Standard Time (UTC+4) scheduling',
                      'UAE-ready compliance',
                    ].map((t) => (
                      <span key={t} className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-400">
                        <Check className="w-3.5 h-3.5 text-amber-300" /> {t}
                      </span>
                    ))}
                  </div>

                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      onClick={() => navigate('/customers/fun-beach-water-sports')}
                      data-testid="fbws-spotlight-story-btn"
                      className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-amber-400 text-black font-semibold hover:bg-amber-300 transition-colors"
                    >
                      Read their story <ArrowRight className="w-4 h-4" />
                    </button>
                    <a
                      href="https://funbeachwatersports.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="fbws-spotlight-visit-btn"
                      className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white/10 hover:bg-white/15 text-white font-medium transition-colors backdrop-blur"
                    >
                      Visit funbeachwatersports.com <Globe className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
            </div>
            <div
              className={`col-start-1 row-start-1 transition-opacity duration-700 ${spotSlide === 2 ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
              aria-hidden={spotSlide !== 2}
              data-testid="spotlight-slide-bbr"
            >
            <div className="relative rounded-[2rem] overflow-hidden bg-gradient-to-br from-neutral-950 via-neutral-900 to-black text-white shadow-[0_40px_90px_-35px_rgba(0,0,0,0.65)]">
              {/* dot texture + yellow glow */}
              <div className="absolute inset-0 opacity-[0.15] pointer-events-none"
                   style={{ backgroundImage: "radial-gradient(circle at 1px 1px, rgba(255,221,51,0.5) 1px, transparent 0)", backgroundSize: "30px 30px" }} />
              <div className="absolute -top-24 -right-16 w-96 h-96 rounded-full bg-yellow-400/15 blur-3xl pointer-events-none" />

              <div className="relative grid lg:grid-cols-2 gap-12 lg:gap-16 items-center p-7 sm:p-10 lg:p-14">
                {/* Left — copy */}
                <div>
                  <div className="flex justify-center lg:justify-start mb-6">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-yellow-400/15 text-[11px] font-semibold uppercase tracking-widest text-yellow-200">
                      <Star className="w-3.5 h-3.5 fill-yellow-300 text-yellow-300" /> Newest on BookingFlows
                    </div>
                  </div>

                  <h2 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold tracking-tight leading-[1.08] mb-4" dir={isAr ? 'rtl' : undefined}>
                    {isAr ? <>نرحّب بـ<span className="bg-gradient-to-r from-yellow-300 to-amber-400 bg-clip-text text-transparent">Banana Boat Rentals</span> — أكبر أسطول قوارب حفلات في أوستن.</> : <>Welcoming <span className="bg-gradient-to-r from-yellow-300 to-amber-400 bg-clip-text text-transparent">Banana Boat Rentals</span> — Austin's largest party-boat fleet.</>}
                  </h2>
                  <p className="text-base sm:text-lg text-gray-300 leading-relaxed mb-7 max-w-xl" dir={isAr ? 'rtl' : undefined}>
                    {isAr ? 'نفخر بتشغيل أكبر أسطول في أوستن. أكثر من 15 قارباً في بحيرتي أوستن وترافيس تعمل الآن على BookingFlows — مع موقع إلكتروني حديث وأنيق وتحسين محركات بحث محلي بنيناه من الصفر. شكراً لثقتكم بنا في إدارة العملية.' : <>We're proud to power Austin's biggest fleet. 15+ boats across Lake Austin
                    and Lake Travis now run on BookingFlows — paired with a clean, modern
                    website and local SEO we built from the ground up. Thank you for trusting
                    us with the operation.</>}
                  </p>

                  <div className="flex flex-wrap gap-2.5 mb-8">
                    {[
                      { icon: Anchor, label: '15+ party boats' },
                      { icon: Star,   label: '500+ 5-star reviews' },
                      { icon: MapPin, label: 'Lake Austin & Travis' },
                    ].map(({ icon: Icon, label }) => (
                      <span key={label} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 text-sm text-gray-200">
                        <Icon className="w-3.5 h-3.5 text-yellow-300" /> {label}
                      </span>
                    ))}
                  </div>

                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      onClick={() => navigate('/customers/banana-boat-rentals')}
                      data-testid="bbr-spotlight-story-btn"
                      className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-yellow-400 text-black font-semibold hover:bg-yellow-300 transition-colors"
                    >
                      Read their story <ArrowRight className="w-4 h-4" />
                    </button>
                    <a
                      href="https://www.bananaboatrentals.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="bbr-spotlight-visit-btn"
                      className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white/10 hover:bg-white/15 text-white font-medium transition-colors backdrop-blur"
                    >
                      Visit bananaboatrentals.com <Globe className="w-4 h-4" />
                    </a>
                  </div>
                </div>

                {/* Right — floating collage */}
                <div className="relative">
                  <div className="grid grid-cols-2 grid-rows-3 gap-3 aspect-[4/5]">
                    <img
                      src={proxyImg("https://1iynnenkjdavmviy.public.blob.vercel-storage.com/bbr_hero_bridge.jpg", 800, 80)}
                      alt="Banana Boat Rentals guests with the Pennybacker Bridge on Lake Austin"
                      className="col-span-2 row-span-2 w-full h-full object-cover rounded-2xl shadow-xl ring-1 ring-white/10"
                      loading="lazy"
                      decoding="async"
                    />
                    <img
                      src={proxyImg("https://1iynnenkjdavmviy.public.blob.vercel-storage.com/bbr_party_group.jpg", 500, 80)}
                      alt="A large group celebrating on a Banana Boat Rentals party boat"
                      className="w-full h-full object-cover rounded-2xl shadow-xl ring-1 ring-white/10"
                      loading="lazy"
                      decoding="async"
                    />
                    <img
                      src={proxyImg("https://1iynnenkjdavmviy.public.blob.vercel-storage.com/bbr_yellow_pontoon.jpg", 500, 80)}
                      alt="A bright yellow Banana Boat Rentals pontoon cruising Lake Austin"
                      className="w-full h-full object-cover rounded-2xl shadow-xl ring-1 ring-white/10"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>

                  {/* floating Banana Boat Rentals logo — top-left */}
                  <div className="absolute -top-3 -left-2 sm:-left-4 z-10 bg-black rounded-xl ring-1 ring-white/15 px-2.5 py-1.5 shadow-2xl">
                    <img src="https://1iynnenkjdavmviy.public.blob.vercel-storage.com/bbr_logo.png"
                         alt="Banana Boat Rentals" className="h-7 sm:h-9 w-auto object-contain" loading="lazy" />
                  </div>
                  {/* floating fleet pill */}
                  <div className="absolute -top-3 -right-2 sm:-right-4 bg-yellow-400 text-black rounded-xl px-3.5 py-2 font-bold text-sm shadow-2xl flex items-center gap-1.5">
                    <Anchor className="w-4 h-4" /> 15+ boats
                  </div>
                  {/* floating review pill */}
                  <div className="absolute -bottom-4 -left-2 sm:-left-5 bg-white text-gray-900 rounded-2xl shadow-2xl px-4 py-3 flex items-center gap-2.5 max-w-[260px]">
                    <div className="flex">
                      {[0,1,2,3,4].map(i => <Star key={i} className="w-3 h-3 fill-amber-400 text-amber-400" />)}
                    </div>
                    <div className="text-xs leading-tight">
                      <div className="font-semibold">500+ Google reviews</div>
                      <div className="text-gray-500">Austin's largest fleet</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            </div>
            <div
              className={`col-start-1 row-start-1 transition-opacity duration-700 ${spotSlide === 3 ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
              aria-hidden={spotSlide !== 3}
              data-testid="spotlight-slide-boatx"
            >
              <div className="relative rounded-[2rem] overflow-hidden bg-gradient-to-br from-[#031619] via-[#06333a] to-black text-white shadow-[0_40px_90px_-35px_rgba(0,0,0,0.65)]">
                {/* dot texture + teal glow */}
                <div className="absolute inset-0 opacity-[0.15] pointer-events-none"
                     style={{ backgroundImage: "radial-gradient(circle at 1px 1px, rgba(45,212,191,0.5) 1px, transparent 0)", backgroundSize: "30px 30px" }} />
                <div className="absolute -top-24 -right-16 w-96 h-96 rounded-full bg-teal-400/15 blur-3xl pointer-events-none" />

                <div className="relative grid lg:grid-cols-2 gap-12 lg:gap-16 items-center p-7 sm:p-10 lg:p-14">
                  {/* Left — copy */}
                  <div>
                    <div className="flex justify-center lg:justify-start mb-6">
                      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-400/15 text-[11px] font-semibold uppercase tracking-widest text-teal-200" data-testid="boatx-spotlight-badge">
                        <Anchor className="w-3.5 h-3.5 text-teal-300" /> Powered end to end
                      </div>
                    </div>

                    <h2 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold tracking-tight leading-[1.08] mb-4" dir={isAr ? 'rtl' : undefined}>
                      {isAr ? <>نرحّب بـ<span className="bg-gradient-to-r from-teal-300 to-cyan-400 bg-clip-text text-transparent">BOATX</span> — بونتونات حفلات مخصّصة على بحيرتي أوستن وترافيس.</> : <>Welcoming <span className="bg-gradient-to-r from-teal-300 to-cyan-400 bg-clip-text text-transparent">BOATX</span> — custom party pontoons on Lake Austin &amp; Lake Travis.</>}
                    </h2>
                    <p className="text-base sm:text-lg text-gray-300 leading-relaxed mb-7 max-w-xl" dir={isAr ? 'rtl' : undefined}>
                      {isAr ? 'أكثر من 1,037 تقييم خمس نجوم على Google وأكثر القوارب إبهاراً على الماء — طوابق مزدوجة، وزلاقات، وحتى بونتون بنقشة الفهد. من boatx.io (صمّمه وبناه BookingFlows) إلى الرصيف، كل حجز وتنازل ودفعة تعمل على المنصة.' : <>1,037+ five-star Google reviews and the wildest boats on the water —
                      double-deckers, slides, even a leopard-print pontoon. From boatx.io
                      (designed and built by BookingFlows) to the dock, every booking,
                      waiver, and payment runs on the platform.</>}
                    </p>

                    <div className="flex flex-wrap gap-2.5 mb-6">
                      {[
                        { icon: Anchor, label: 'Custom double-deckers' },
                        { icon: Star,   label: '1,037+ 5-star reviews' },
                        { icon: MapPin, label: 'Lake Austin & Travis' },
                      ].map(({ icon: Icon, label }) => (
                        <span key={label} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 text-sm text-gray-200">
                          <Icon className="w-3.5 h-3.5 text-teal-300" /> {label}
                        </span>
                      ))}
                    </div>

                    <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mb-8" data-testid="boatx-chips">
                      {[
                        'Website + bookings, one team',
                        'Embedded widget live on boatx.io',
                        'Gift cards & group events',
                      ].map((t) => (
                        <span key={t} className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-400">
                          <Check className="w-3.5 h-3.5 text-teal-300" /> {t}
                        </span>
                      ))}
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                      <button
                        type="button"
                        onClick={() => navigate('/customers/boatx')}
                        data-testid="boatx-spotlight-story-btn"
                        className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-teal-400 text-black font-semibold hover:bg-teal-300 transition-colors"
                      >
                        Read their story <ArrowRight className="w-4 h-4" />
                      </button>
                      <a
                        href="https://www.boatx.io/"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="boatx-spotlight-visit-btn"
                        className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white/10 hover:bg-white/15 text-white font-medium transition-colors backdrop-blur"
                      >
                        Visit boatx.io <Globe className="w-4 h-4" />
                      </a>
                    </div>
                  </div>

                  {/* Right — floating collage */}
                  <div className="relative">
                    <div className="grid grid-cols-2 grid-rows-3 gap-3 aspect-[4/5]">
                      <img
                        src="/images/boatx-hero.webp"
                        alt="A BOATX double-decker party pontoon loaded with a birthday crew on Lake Austin"
                        className="col-span-2 row-span-2 w-full h-full object-cover rounded-2xl shadow-xl ring-1 ring-white/10"
                        loading="lazy"
                        decoding="async"
                      />
                      <img
                        src="/images/boatx-leopard.webp"
                        alt="The BOATX leopard-print pontoon cruising Lake Austin"
                        className="w-full h-full object-cover rounded-2xl shadow-xl ring-1 ring-white/10"
                        loading="lazy"
                        decoding="async"
                      />
                      <img
                        src="/images/boatx-sunset.webp"
                        alt="Sunset aboard a BOATX pontoon on Lake Travis"
                        className="w-full h-full object-cover rounded-2xl shadow-xl ring-1 ring-white/10"
                        loading="lazy"
                        decoding="async"
                      />
                    </div>

                    {/* floating BOATX logo — top-left */}
                    <div className="absolute -top-3 -left-2 sm:-left-4 z-10 bg-[#052e33] rounded-xl ring-1 ring-white/15 px-3 py-2 shadow-2xl">
                      <img src="/images/boatx-logo.webp"
                           alt="BOATX" className="h-6 sm:h-7 w-auto object-contain" loading="lazy" />
                    </div>
                    {/* floating location pill */}
                    <div className="absolute -top-3 -right-2 sm:-right-4 bg-teal-400 text-black rounded-xl px-3.5 py-2 font-bold text-sm shadow-2xl flex items-center gap-1.5">
                      <MapPin className="w-4 h-4" /> Austin · TX
                    </div>
                    {/* floating review pill */}
                    <div className="absolute -bottom-4 -left-2 sm:-left-5 bg-white text-gray-900 rounded-2xl shadow-2xl px-4 py-3 flex items-center gap-2.5 max-w-[260px]">
                      <div className="flex">
                        {[0,1,2,3,4].map(i => <Star key={i} className="w-3 h-3 fill-amber-400 text-amber-400" />)}
                      </div>
                      <div className="text-xs leading-tight">
                        <div className="font-semibold">1,037+ Google reviews</div>
                        <div className="text-gray-500">5.0 · Lake Austin</div>
                      </div>
                    </div>
                    {/* floating website pill */}
                    <div className="absolute -bottom-4 -right-2 sm:-right-5 bg-white text-gray-900 rounded-2xl shadow-2xl px-4 py-3 flex items-center gap-2.5 max-w-[260px]">
                      <Globe className="w-6 h-6 text-[#052e33] shrink-0" />
                      <div className="text-xs leading-tight">
                        <div className="font-semibold">boatx.io</div>
                        <div className="text-gray-500">Built by BookingFlows</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* carousel dots */}
          <div className="flex items-center justify-center gap-2.5 mt-10">
            {[
              ['spotlight-dot-0', 'Show Trail Blazer Yachts (Dubai, UAE)'],
              ['spotlight-dot-1', 'Show Fun Beach Water Sports (Dubai, UAE)'],
              ['spotlight-dot-2', 'Show Banana Boat Rentals (Austin, TX)'],
              ['spotlight-dot-3', 'Show BOATX (Lake Austin & Lake Travis, TX)'],
            ].map(([tid, label], i) => (
              <button
                key={tid}
                type="button"
                onClick={() => setSpotSlide(i)}
                data-testid={tid}
                aria-label={label}
                className={`h-2 rounded-full transition-all duration-300 ${spotSlide === i ? 'w-8 bg-gray-900' : 'w-2 bg-gray-300 hover:bg-gray-400'}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ───────────────────────────────────────────────────────────────
          Marine weather — tide, wind, solar & moon built into the platform.
          Captains see it in the dashboard; customers get a trip-day report.
      ─────────────────────────────────────────────────────────────── */}
      <section className="py-24 bg-white" data-testid="supplier-weather-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* Left — copy */}
            <div className="animate-on-scroll">
              <div className="inline-flex items-center gap-2 text-sky-600 text-xs font-semibold uppercase tracking-[0.2em] mb-4">
                <CloudSun className="w-3.5 h-3.5" />
                Marine weather
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight leading-[1.1]" dir={isAr ? 'rtl' : undefined}>
                {isAr ? <>الطقس والمدّ والقمر.<span className="block text-gray-400">مدمجة في قلب عمليتك.</span></> : <>Weather, tide and moon.
                <span className="block text-gray-400">Built into your operation.</span></>}
              </h2>
              <p className="mt-5 text-lg text-gray-600 leading-relaxed max-w-xl" dir={isAr ? 'rtl' : undefined}>
                {isAr ? 'البحّارة يعيشون على الأحوال الجوية — لذلك وضعناها حيث يعمل طاقمك أصلاً. نظرة واحدة من لوحة التحكم، وتقرير في بريد كل عميل.' : <>Mariners live by the conditions — so we put them where your crew already
                works. One glance from the dashboard, one report in every customer's inbox.</>}
              </p>

              <div className="mt-7 space-y-3.5">
                {[
                  { Icon: Wind, title: 'Live conditions on your dashboard', desc: 'Temperature, wind in knots, gusts and hourly outlook for your exact operating area.' },
                  { Icon: Waves, title: 'Tide & solar aware', desc: 'High and low tide times, sunrise, sunset and UV — your captains see it all before first departure.' },
                  { Icon: Mail, title: 'Morning crew briefing', desc: 'The whole team gets the day\u2019s outlook emailed automatically, every morning.' },
                  { Icon: Moon, title: 'Customers get a trip-day report', desc: 'Included with every booking — a clean forecast for their exact departure time, moon phase and all.' },
                ].map((f) => (
                  <div key={f.title} className="flex items-start gap-3.5">
                    <span className="w-8 h-8 rounded-full bg-sky-50 text-sky-600 flex items-center justify-center shrink-0 mt-0.5">
                      <f.Icon className="w-4 h-4" strokeWidth={1.75} />
                    </span>
                    <div>
                      <span className="font-semibold text-gray-900 text-sm">{f.title}</span>
                      <span className="text-gray-500 text-sm"> — {f.desc}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-4">
                <button
                  onClick={() => { trackSignupStarted('supplier_weather_section'); navigate('/signup'); }}
                  data-testid="supplier-weather-signup"
                  className="group inline-flex items-center gap-2 text-sm font-semibold text-sky-600 hover:text-sky-700 transition-colors"
                >
                  Bring weather aboard
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </button>
                <span className="text-xs text-gray-400 font-medium">
                  Included on every plan. No add-ons.
                </span>
              </div>
            </div>

            {/* Right — weather cards visual */}
            <div className="animate-on-scroll" aria-hidden="true">
              <img
                src="/images/bf-weather-mockup.webp"
                alt="BookingFlows marine weather — tide, wind, moon and solar conditions"
                className="w-full block"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Built by an operator — founder credentials + global advisory.
          The human, trust-building moment of the page. */}
      <section className="py-24 bg-gradient-to-b from-white via-blue-50/60 to-white relative overflow-hidden">
        <div className="absolute top-24 left-0 w-72 h-72 bg-blue-200/30 rounded-full blur-3xl -z-0" aria-hidden="true" />
        <div className="absolute bottom-24 right-0 w-72 h-72 bg-indigo-200/30 rounded-full blur-3xl -z-0" aria-hidden="true" />

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Founder image */}
            <div className="order-2 md:order-1">
              <div className="relative max-w-md mx-auto md:mx-0">
                <img
                  src="/images/founder/founder.jpg"
                  alt="BookingFlows founder"
                  className="rounded-3xl shadow-2xl shadow-blue-500/10 w-full object-cover aspect-square"
                />
                {/* Floating "at the helm" accent — real, not stock */}
                <div className="absolute -top-3 -left-2 sm:-top-5 sm:-left-6 w-[4.5rem] sm:w-24 -rotate-[5deg] z-10">
                  <img
                    src={proxyImg("https://customer-assets.emergentagent.com/job_c73baa61-619c-4b73-9105-ad2ee2a3c85f/artifacts/usx6igta_IMG_7883.jpg", 400, 80)}
                    alt="Ryan at the helm"
                    className="w-full aspect-[3/4] object-cover rounded-2xl shadow-xl ring-4 ring-white"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                {/* Floating "boat lights + whip cream" accent — top right */}
                <div className="absolute -top-3 -right-2 sm:-top-5 sm:-right-6 w-[4.5rem] sm:w-24 rotate-[5deg] z-10">
                  <img
                    src={proxyImg("https://customer-assets.emergentagent.com/job_c73baa61-619c-4b73-9105-ad2ee2a3c85f/artifacts/z12c9tfo_Captain%20Ryan.remini-enhanced%20%281%29.JPG", 400, 80)}
                    alt="Ryan smiling on the boat"
                    className="w-full aspect-[3/4] object-cover rounded-2xl shadow-xl ring-4 ring-white"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                {/* Floating "racing under the Dubai skyline" accent — bottom left */}
                <div className="absolute -bottom-4 -left-2 sm:-bottom-6 sm:-left-7 w-[4.5rem] sm:w-24 -rotate-[4deg] z-10">
                  <img
                    src="/images/founder/racing-dubai.jpg"
                    alt="Sailboats racing under the Dubai skyline"
                    className="w-full aspect-[3/4] object-cover rounded-2xl shadow-xl ring-4 ring-white"
                    loading="lazy"
                    decoding="async"
                  />
                  <span className="absolute bottom-1 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-slate-900/60 backdrop-blur-sm px-1.5 py-0.5 text-[7px] sm:text-[8px] font-semibold text-white tracking-wide" data-testid="founder-racing-dubai-tag">
                    Racing, Dubai
                  </span>
                </div>
                {/* Floating cert badge */}
                <div className="absolute -bottom-4 -right-2 sm:-bottom-5 sm:-right-3 bg-white rounded-xl sm:rounded-2xl shadow-lg px-2.5 py-1.5 sm:px-4 sm:py-3 flex items-center gap-2 sm:gap-3">
                  <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-full bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center flex-shrink-0">
                    <Shield className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
                  </div>
                  <div>
                    <div className="text-[9px] sm:text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Licensed Captain</div>
                    <div className="text-[11px] sm:text-sm font-semibold text-gray-900">USCG · RYA · MCA · IYT · ASA</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Copy */}
            <div className="order-1 md:order-2">
              <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-5">
                <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                Built by an operator
              </div>
              <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-5" dir={isAr ? 'rtl' : undefined}>
                {isAr ? <>صُنع بيد قبطان.<br /><span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">وراجعه مشغّلون حول العالم.</span></> : <>Built by a captain.<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                  Reviewed by operators worldwide.
                </span></>}
              </h3>
              <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-7" dir={isAr ? 'rtl' : undefined}>
                {isAr ? 'BookingFlows ليس مجرد برنامج SaaS عام آخر. بناه قبطان رحلات مرخّص بمؤهلات دولية — وشكّله مشغّلون في فلوريدا والبحر المتوسط والإمارات والكاريبي. كل ميزة تحل مشكلة حقيقية عاشها شخص بيدين خشنتين من العمل.' : <>BookingFlows isn't another generic SaaS. It's built by a licensed charter captain
                with international qualifications — and shaped by operators in Florida, the
                Mediterranean, the UAE and the Caribbean. Every feature solves a real problem
                someone with calloused hands has actually had.</>}
              </p>

              {/* Cert pills */}
              <div className="flex flex-wrap gap-2 mb-7">
                {['USCG', 'RYA', 'MCA', 'IYT', 'ASA'].map((cert) => (
                  <span
                    key={cert}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white text-xs font-semibold text-gray-700 shadow-sm"
                  >
                    <Shield className="w-3 h-3 text-blue-600" />
                    {cert}
                  </span>
                ))}
              </div>

              {/* Stat row — flat */}
              <div className="grid grid-cols-3 gap-3 sm:gap-5">
                <div>
                  <div className="text-2xl font-bold text-gray-900">16+</div>
                  <div className="text-[11px] uppercase tracking-wider text-gray-500 mt-0.5">Certs, Licenses &amp; Endorsements</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">Lab</div>
                  <div className="text-[11px] uppercase tracking-wider text-gray-500 mt-0.5">SWE Background</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">100%</div>
                  <div className="text-[11px] uppercase tracking-wider text-gray-500 mt-0.5">Operator-Built</div>
                </div>
              </div>

              {/* Meet the team link */}
              <button
                onClick={() => navigate('/meet-the-team')}
                className="group mt-7 inline-flex items-center gap-2 text-blue-600 font-semibold hover:text-blue-700 transition-colors"
                data-testid="founder-meet-team-link"
              >
                Meet the team
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>

            </div>
          </div>
        </div>
      </section>
      {/* ───────────────────────────────────────────────────────────────
          HELM Intelligence by BookingFlows — flagship analytics suite.
          Placed top-of-page per founder: market the outcome, not the
          methodology. All numbers below are illustrative.
      ─────────────────────────────────────────────────────────────── */}
      <section className="py-24 bg-white" data-testid="supplier-helm-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-[2rem] bg-[#04091B] text-white">
            {/* Header — sparkline + sparkles under the title */}
            <div className="relative flex flex-col items-center text-center px-6 pt-14 sm:pt-16">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/[0.07] mb-6">
                <img src="/bf-intelligence-logo.png" alt="HELM Intelligence" className="w-4 h-4 object-contain" />
                <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-blue-300">New · Included free with BookingFlows</span>
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.08]" dir={isAr ? 'rtl' : undefined}>
                {isAr ? <>تعرّف على HELM <span className="italic">Intelligence</span>.</> : <>Meet HELM <span className="italic">Intelligence</span>.</>}
              </h2>
              <p className="text-sm font-semibold text-slate-400 tracking-wide mt-2">by BookingFlows</p>
              <div className="relative w-full max-w-lg h-14 mt-1" aria-hidden="true">
                <div className="absolute inset-x-16 top-0 bg-gradient-to-r from-transparent via-blue-500 to-transparent h-[2px] w-[calc(100%-8rem)] blur-sm" />
                <div className="absolute inset-x-16 top-0 bg-gradient-to-r from-transparent via-blue-400 to-transparent h-px w-[calc(100%-8rem)]" />
                <div className="absolute inset-x-28 top-0 bg-gradient-to-r from-transparent via-sky-400 to-transparent h-[4px] w-[calc(100%-14rem)] blur-sm" />
                <SparklesCore background="transparent" minSize={0.4} maxSize={1} particleDensity={900} particleColor="#93C5FD" className="w-full h-full" />
                <div className="absolute inset-0 w-full h-full bg-[#04091B] [mask-image:radial-gradient(260px_90px_at_top,transparent_20%,white)]" />
              </div>
              <p className="max-w-2xl text-base sm:text-lg text-slate-300 leading-relaxed mt-2 pb-2" dir={isAr ? 'rtl' : undefined}>
                {isAr ? 'بياناتك، مفكّكة الشيفرة. يقرأ HELM كل رحلة تديرها ويحوّلها إلى خطوات واضحة — كم يجني كل قارب فعلاً، وأي الخدمات تُباع، ومتى يبلغ الطلب ذروته، ومن يعود إليك باستمرار.' : <>Your booking data, decoded. HELM reads every trip you run and turns it
                into clear moves — what each boat really earns, which services sell,
                when demand peaks, and who keeps coming back.</>}
              </p>
            </div>

            {/* Body — feature tiles + framed render */}
            <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center px-6 sm:px-10 lg:px-14 pb-12 sm:pb-14 pt-8">
              <div>
                <div className="grid sm:grid-cols-2 gap-3.5 mb-6">
                  {[
                    { icon: Anchor, title: 'Asset Earnings', desc: 'Revenue, rev/hour and seats filled — per boat.' },
                    { icon: TrendingUp, title: 'Revenue Mix', desc: 'What actually sells, realized prices and add-on attach.' },
                    { icon: Calendar, title: 'Demand Center', desc: 'Peak days, start times and how far ahead customers book.' },
                    { icon: Users, title: 'Customer Insights', desc: 'Repeat rates, group sizes and your highest-value guests.' },
                  ].map((f) => (
                    <div key={f.title} className="rounded-2xl bg-white/[0.06] p-5" data-testid={'helm-tile-' + f.title.toLowerCase().replace(/\s+/g, '-')}>
                      <span className="w-9 h-9 rounded-xl bg-blue-500/15 text-blue-300 flex items-center justify-center mb-3">
                        <f.icon className="w-4 h-4" strokeWidth={1.75} />
                      </span>
                      <div className="font-semibold text-white text-sm">{f.title}</div>
                      <div className="text-slate-400 text-[13px] leading-relaxed mt-1">{f.desc}</div>
                    </div>
                  ))}
                </div>

                <div className="flex items-start gap-3 rounded-2xl bg-white/[0.06] p-4 mb-7">
                  <Sparkles className="w-4 h-4 text-blue-300 mt-0.5 shrink-0" />
                  <p className="text-sm text-slate-300 leading-relaxed">
                    <span className="font-semibold text-white">AI Suggestions built in.</span>{' '}
                    HELM spots the gaps and hands you one clear move — read-only, from your data only.
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-4">
                  <button
                    onClick={() => { trackSignupStarted('supplier_helm_section'); navigate('/signup'); }}
                    className="group inline-flex items-center gap-2 text-sm font-semibold text-blue-300 hover:text-blue-200 transition-colors"
                    data-testid="helm-section-cta"
                  >
                    Start free — HELM included
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </button>
                  <span className="text-xs text-slate-500 font-medium">Your data only · No setup · Every plan</span>
                </div>
              </div>

              {/* Framed render */}
              <div className="rounded-2xl bg-white p-3 sm:p-4" aria-hidden="true">
                <img
                  src="/images/bf-helm.webp"
                  alt="HELM Intelligence dashboard — revenue overview, fleet earnings, funnel and customer insights with the mobile companion"
                  loading="lazy"
                  data-testid="helm-section-visual"
                  className="w-full block rounded-xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───────────────────────────────────────────────────────────────
          The nutshell — what BookingFlows is, in three sentences.
      ─────────────────────────────────────────────────────────────── */}
      <section className="py-24 bg-white" data-testid="supplier-nutshell-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-6">
            <Anchor className="w-3.5 h-3.5" />
            BookingFlows, in a nutshell
          </div>
          <img
            src="/images/bf-overview.webp"
            alt="BookingFlows platform overview — reservations, waivers, weather, fleet, finance, crew, payments, analytics, AI and advertising around one dashboard"
            loading="lazy"
            data-testid="nutshell-section-visual"
            className="w-full block max-w-5xl mx-auto mb-14"
          />
          <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.12] mb-8" dir={isAr ? 'rtl' : undefined}>
            {isAr ? <>برنامج حجوزات بمستوى تشغيلي{' '}<span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">للبحّارة.</span></> : <>Operations-grade booking software{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">for mariners.</span></>}
          </h3>
          <p className="text-lg sm:text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto" dir={isAr ? 'rtl' : undefined}>
            {isAr ? 'الحجوزات، والتنازلات، والطقس، والطاقم، والمدفوعات، والتحليلات، والذكاء الاصطناعي — منصة واحدة تدير العملية كاملة. صُممت لشركات الرحلات البحرية وتأجير القوارب وكل من يعمل على الماء.' : <>Reservations, waivers, weather, crew, payments, analytics and AI — one platform
            that runs the whole operation. Built for charter companies, boat rentals, and
            everyone who works on the water.</>}
          </p>
        </div>
      </section>

      {/* 🔒 Data trust — answers the "are you going to steal my customers?"
          objection head-on. Sits right after migration (where operators hand
          over their data) — the dark Enterprise-grade Security section lower
          down stays as-is. */}
      <section className="py-24 bg-white" data-testid="supplier-data-trust-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left — copy */}
            <div>
              <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-5">
                <Lock className="w-3.5 h-3.5" />
                Your data · Siloed &amp; secure
              </div>
              <h3 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1] mb-5" dir={isAr ? 'rtl' : undefined}>
                {isAr ? <>بياناتك ملكك.{' '}<span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">نقطة، انتهى.</span></> : <>Your data is yours.{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">Full stop.</span></>}
              </h3>
              <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-8 max-w-xl" dir={isAr ? 'rtl' : undefined}>
                {isAr ? 'كل شركة على BookingFlows تعمل في نطاق معزول خاص بها — عملاؤك وحجوزاتك وإيراداتك مرئية لك وحدك. لا تُباع أبداً، ولا تُشارك، ولا تُستخدم لمنافستك. نحن شركة برمجيات: عملنا الوحيد هو اشتراكك، لا قائمة عملائك.' : <>Every company on BookingFlows runs in its own silo — your customers, bookings and
                revenue are visible to you and no one else. Never sold, never shared, never used
                to compete with you. Our only business is your subscription, not your customer list.</>}
              </p>
              <div className="grid sm:grid-cols-2 gap-3.5 mb-8" data-testid="data-trust-tiles">
                {[
                  { icon: Database, t: 'Siloed per company', d: 'Hard tenant isolation on every query.' },
                  { icon: Lock, t: 'AES-256 · TLS 1.3', d: 'Encrypted at rest and in transit.' },
                  { icon: Fingerprint, t: 'RBAC, 2FA & FIDO2', d: 'Role-scoped access, hardware-key ready.' },
                  { icon: Download, t: 'Export anytime', d: 'Your data leaves with you, on demand.' },
                ].map((f) => (
                  <div key={f.t} className="rounded-2xl bg-gray-50 p-4">
                    <div className="flex items-center gap-2.5 mb-1">
                      <f.icon className="w-4 h-4 text-blue-600 shrink-0" strokeWidth={2} />
                      <span className="text-sm font-bold text-gray-900">{f.t}</span>
                    </div>
                    <p className="text-[13px] text-gray-500 leading-relaxed">{f.d}</p>
                  </div>
                ))}
              </div>
              <button
                onClick={() => navigate('/security')}
                className="group inline-flex items-center gap-2 text-sm font-semibold text-[#2563EB] hover:text-[#1D4ED8] transition-colors"
                data-testid="data-trust-section-cta"
              >
                Read how we protect your data
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
            </div>
            {/* Right — Evervault encrypted-data card (Aceternity): hover shows
                the ciphertext your data looks like to anyone who isn't you. */}
            <div className="relative flex justify-center animate-on-scroll min-w-0" data-testid="data-trust-section-visual">
              <div className="border border-black/[0.2] flex flex-col items-start w-full max-w-sm min-w-0 mx-auto p-4 relative h-[26rem] sm:h-[28rem]">
                <EvervaultIcon className="absolute h-6 w-6 -top-3 -left-3 text-black" />
                <EvervaultIcon className="absolute h-6 w-6 -bottom-3 -left-3 text-black" />
                <EvervaultIcon className="absolute h-6 w-6 -top-3 -right-3 text-black" />
                <EvervaultIcon className="absolute h-6 w-6 -bottom-3 -right-3 text-black" />
                <EvervaultCard text="AES-256" />
                <h4 className="text-gray-600 mt-4 text-sm font-light" dir={isAr ? 'rtl' : undefined}>
                  {isAr ? 'مرّر المؤشر فوق البطاقة — هكذا تبدو بياناتك لأي شخص غيرك. مشفّرة أثناء التخزين وأثناء النقل.' : "Hover the card — this is what your data looks like to anyone who isn't you. Encrypted at rest and in transit."}
                </h4>
                <p className="text-sm border font-light border-black/[0.2] rounded-full mt-4 text-gray-900 px-2.5 py-0.5">
                  {isAr ? 'مشفّرة من طرف إلى طرف' : 'Encrypted end-to-end'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Mobile App Section — operator-focused, mirrors /marketplace styling */}
      <section className="py-24 px-4 bg-white animate-on-scroll" data-testid="supplier-app-store-section">
        <div className="max-w-5xl mx-auto">
          <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-3xl overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center p-8 md:p-12">
              <div className="text-white">
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 text-xs font-semibold tracking-wide mb-5">
                  <Download className="w-3.5 h-3.5" /> NOW ON iOS
                </span>
                <h2 className="text-2xl sm:text-3xl font-bold mb-4">
                  Take BookingFlows with you
                </h2>
                <p className="text-blue-100 mb-6">
                  Run your boat business from your pocket. Manage bookings, check your daily schedule, and get instant alerts the moment a new booking comes in — wherever you are.
                </p>
                <div className="flex flex-wrap gap-4">
                  {/* App Store Button — real link */}
                  <a
                    href="https://apps.apple.com/us/app/bookingflows/id6757174499"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 bg-black hover:bg-gray-900 px-5 py-3 rounded-xl transition-colors"
                    data-testid="supplier-app-store-link"
                  >
                    <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                    </svg>
                    <div className="text-left">
                      <p className="text-xs text-white/70">Download on the</p>
                      <p className="text-sm font-semibold">App Store</p>
                    </div>
                  </a>

                  {/* Play Store Button — coming soon */}
                  <div className="flex items-center gap-3 bg-black/40 px-5 py-3 rounded-xl cursor-not-allowed opacity-75">
                    <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                    </svg>
                    <div className="text-left">
                      <p className="text-xs text-white/70">Coming Soon</p>
                      <p className="text-sm font-semibold">Google Play</p>
                    </div>
                  </div>
                </div>

                {/* What you can do — under app buttons */}
                <div className="mt-8 pt-6 border-t border-white/20">
                  <p className="text-sm font-medium text-white mb-4">What you can do</p>
                  <div className="flex flex-col gap-3 text-sm text-blue-100">
                    <span className="flex items-center gap-2">
                      <Activity className="w-4 h-4" />
                      Manage and reassign bookings on the go
                    </span>
                    <span className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      See your daily schedule at a glance
                    </span>
                    <span className="flex items-center gap-2">
                      <Bell className="w-4 h-4" />
                      Instant push alerts the moment a booking lands
                    </span>
                  </div>
                </div>
              </div>

              {/* Phone mockup */}
              <div className="hidden md:flex justify-center">
                <div className="relative">
                  <div className="w-64 h-[500px] bg-gray-900 rounded-[3rem] p-2 shadow-2xl">
                    <div className="w-full h-full bg-white rounded-[2.5rem] overflow-hidden flex items-center justify-center">
                      <div className="text-center p-6">
                        <img
                          src="/logo-icon.png"
                          alt="BookingFlows"
                          className="w-20 h-20 mx-auto mb-4 rounded-2xl shadow-lg"
                        />
                        <p className="font-bold text-gray-900 text-lg">BookingFlows</p>
                        <p className="text-gray-500 text-sm mt-1">Your fleet, in your pocket</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Built in the USA & UAE — world map with Austin ↔ Dubai arcs */}
      <section className="py-24 bg-white animate-on-scroll" data-testid="supplier-usa-uae-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-4" dir={isAr ? 'rtl' : undefined}>
              <Globe className="w-3.5 h-3.5" />
              {isAr ? 'قارّتان. فريق واحد.' : 'Two Continents. One Team.'}
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4 tracking-tight" dir={isAr ? 'rtl' : undefined}>
              {isAr ? 'صُنع بفخر في أمريكا والإمارات' : <>Proudly built in the USA &amp; UAE</>}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed" dir={isAr ? 'rtl' : undefined}>
              {isAr
                ? 'فريق BookingFlows يمتد بين الولايات المتحدة والإمارات العربية المتحدة — بُني على الماء، لمشغّلين على الماء، في اثنين من أكبر أسواق القوارب في العالم.'
                : 'BookingFlows is engineered by a team spanning the United States and the United Arab Emirates — built on the water, for operators on the water, in two of the world\u2019s biggest boating markets.'}
            </p>
          </div>

          <div data-testid="usa-uae-world-map">
            <WorldMap
              lineColor="#2563EB"
              dots={[
                { start: { lat: 30.2672, lng: -97.7431 }, end: { lat: 25.2048, lng: 55.2708 } },
                { start: { lat: 30.2672, lng: -97.7431 }, end: { lat: 25.7617, lng: -80.1918 } },
                { start: { lat: 25.2048, lng: 55.2708 }, end: { lat: 37.9838, lng: 23.7275 } },
                { start: { lat: 25.7617, lng: -80.1918 }, end: { lat: 18.4655, lng: -66.1057 } },
              ]}
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-4 mt-6 mb-8">
            <div className="rounded-2xl bg-gray-50 p-6 flex items-start gap-4" data-testid="usa-hq-card">
              <img
                src="https://customer-assets.emergentagent.com/job_a2831e1f-cd94-4900-9f8b-f2b06f8d9d25/artifacts/oxdfkldx_Flag-United-States-of-America.webp"
                alt="Flag of the United States"
                className="w-14 h-9 rounded-md object-cover shrink-0"
                loading="lazy"
              />
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-base font-bold text-gray-900">Austin, Texas</h3>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-blue-700 bg-blue-600/10 rounded-full px-2 py-0.5">HQ</span>
                </div>
                <p className="text-sm text-gray-500 leading-relaxed">Product, engineering and operator success — headquartered on Lake Travis, built alongside real Texas fleets.</p>
              </div>
            </div>
            <div className="rounded-2xl bg-gray-50 p-6 flex items-start gap-4" data-testid="uae-hq-card">
              <img
                src="https://customer-assets.emergentagent.com/job_a2831e1f-cd94-4900-9f8b-f2b06f8d9d25/artifacts/vjzngumg_Flag-United-Arab-Emirates.webp"
                alt="Flag of the United Arab Emirates"
                className="w-14 h-9 rounded-md object-cover shrink-0"
                loading="lazy"
              />
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-base font-bold text-gray-900">Dubai, UAE</h3>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-600/10 rounded-full px-2 py-0.5">HQ</span>
                </div>
                <p className="text-sm text-gray-500 leading-relaxed">Serving the Gulf’s charter scene — AED-ready, WhatsApp-first, and tuned for the marinas of the Emirates.</p>
              </div>
            </div>
          </div>

          {/* Follow-the-sun strip */}
          <div className="rounded-2xl bg-gray-50 px-6 py-5 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-8 text-center sm:text-left" data-testid="follow-the-sun-strip">
            <div className="flex items-center gap-2 text-gray-900 font-semibold">
              <Clock className="w-5 h-5 text-blue-600" />
              Support that follows the sun
            </div>
            <p className="text-sm text-gray-600">
              With teams 9 time zones apart, someone is always awake when your customers are booking — from Austin sunsets to Dubai sunrises.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ — before the final CTA */}
      <FaqSection isAr={isAr} onContact={() => navigate('/support')} />

      {/* Final CTA — background beams with collision */}
      <BackgroundBeamsWithCollision className="min-h-[34rem] py-28" data-testid="supplier-final-cta">
        <div className="max-w-4xl mx-auto text-center px-4 relative z-20">
          <div className="inline-flex items-center gap-2 text-blue-600 text-xs font-semibold uppercase tracking-[0.2em] mb-6" dir={isAr ? 'rtl' : undefined}>
            <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
            {isAr ? 'برنامج الحجز الأعلى تقييماً' : 'Top-Rated Booking Software'}
          </div>
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-[1.05] tracking-tight" dir={isAr ? 'rtl' : undefined}>
            {isAr ? <>سجّل في برنامج الحجوزات<br className="hidden sm:block" /><span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500"> — ابدأ مجاناً</span></> : <>Sign up for booking software<br className="hidden sm:block" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500"> — free to start</span></>}
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 mb-4 max-w-2xl mx-auto leading-relaxed" dir={isAr ? 'rtl' : undefined}>
            {isAr
              ? 'انضم إلى مئات مشغّلي الجولات وشركات الرحلات البحرية والتأجير الذين يستقبلون الحجوزات أونلاين على مدار الساعة.'
              : 'Join hundreds of tour operators, charter companies, and rental businesses taking bookings online 24/7.'}
          </p>
          <p className="text-sm text-blue-600 mb-10" dir={isAr ? 'rtl' : undefined}>
            {isAr ? 'بعد التسجيل، سيتواصل معك فريقنا شخصياً لمساعدتك في الإعداد.' : 'After you sign up, our team will reach out personally to help you get set up.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <NoiseBackground
              containerClassName="w-fit p-[5px] rounded-full"
              gradientColors={["rgb(37, 99, 235)", "rgb(125, 211, 252)", "rgb(29, 78, 216)"]}
            >
              <button
                onClick={() => { trackSignupStarted('supplier_cta_bottom'); navigate('/signup'); }}
                className="inline-flex items-center cursor-pointer rounded-full bg-blue-600 hover:bg-blue-500 px-10 py-4 text-lg font-medium text-white shadow-[0px_2px_0px_0px_rgba(255,255,255,0.22)_inset] transition-[background-color,transform] duration-150 active:scale-[0.98]"
                data-testid="supplier-cta-bottom"
              >
                Sign Up for Booking Software <ArrowRight className="ml-3 w-5 h-5" />
              </button>
            </NoiseBackground>
            <button
              onClick={() => navigate('/demo')}
              className="px-10 py-4 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-800 text-lg font-medium transition-colors"
              data-testid="supplier-cta-demo"
            >
              {isAr ? 'احجز عرضاً مجانياً' : 'Book a Free Demo'}
            </button>
          </div>
          <p className="text-gray-400 text-sm mt-6">
            {isAr ? 'لا حاجة لبطاقة ائتمان · مجاني للبدء · نقل بيانات مجاني · إعداد خلال 15 دقيقة' : 'No credit card required · Free to start · Free data migration · Setup in 15 minutes'}
          </p>
        </div>
      </BackgroundBeamsWithCollision>

      {/* Footer — full footer mirroring /marketplace for brand congruence.
          Includes social links + customer story link + marketplace cross-link. */}
      <footer className="bg-gray-900 text-gray-400 py-14 pb-[calc(3.5rem+env(safe-area-inset-bottom))]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-12">
            {/* Brand blurb */}
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <img
                  src="/logo-icon.png"
                  alt="BookingFlows"
                  className="w-9 h-9 rounded-lg object-cover"
                />
                <h4 className="text-white font-bold text-lg">BookingFlows</h4>
              </div>
              <p className={`text-sm leading-relaxed ${isAr ? 'text-right' : ''}`} dir={isAr ? 'rtl' : undefined}>
                {isAr
                  ? 'برنامج حجوزات أونلاين للجولات والفعاليات والتجارب وكل نشاط يستقبل الحجوزات.'
                  : 'Online booking software for tours, venues, experiences, and any business that takes reservations.'}
              </p>
              {/* Social row — same set as /marketplace */}
              <div className="mt-5">
                <SocialLinksDark />
              </div>
            </div>

            {/* Product */}
            <div>
              <h5 className="text-white font-semibold mb-4">{isAr ? 'المنتج' : 'Product'}</h5>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate('/features')} className="hover:text-white transition-colors text-left" data-testid="footer-features-link">All Features</button></li>
                <li><a href="/features#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><button onClick={() => navigate('/customers/boatx')} className="hover:text-white transition-colors text-left">BOATX story</button></li>
                <li><button onClick={() => navigate('/customers/banana-boat-rentals')} className="hover:text-white transition-colors text-left">Banana Boat Rentals story</button></li>
                <li><button onClick={() => navigate('/marketplace')} className="hover:text-white transition-colors text-left">Marketplace</button></li>
              </ul>
            </div>

            {/* Software */}
            <div>
              <h5 className="text-white font-semibold mb-4">{isAr ? 'البرنامج' : 'Software'}</h5>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate('/for-suppliers')} className="hover:text-white transition-colors text-left">Booking Software</button></li>
                <li><button onClick={() => navigate('/for-charters')} className="hover:text-white transition-colors text-left">Charter Booking</button></li>
                <li><button onClick={() => navigate('/for-tours')} className="hover:text-white transition-colors text-left">Tour Booking</button></li>
                <li><button onClick={() => navigate('/for-rentals')} className="hover:text-white transition-colors text-left">Rental Booking</button></li>
              </ul>
            </div>

            {/* Resources */}
            <div>
              <h5 className="text-white font-semibold mb-4">{isAr ? 'الموارد' : 'Resources'}</h5>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate('/demo')} className="hover:text-white transition-colors text-left" data-testid="footer-demo-link">Book a Demo</button></li>
                <li><button onClick={() => navigate('/referral-program')} className="hover:text-white transition-colors text-left" data-testid="footer-referral-link">Referral Program</button></li>
                <li><button onClick={() => navigate('/onboarding')} className="hover:text-white transition-colors text-left" data-testid="footer-onboarding-link">Onboarding Guide</button></li>
                <li><button onClick={() => navigate('/developers')} className="hover:text-white transition-colors text-left" data-testid="footer-api-docs-link">API Docs</button></li>
                <li><button onClick={() => navigate('/find-booking')} className="hover:text-white transition-colors text-left">Find My Booking</button></li>
                <li><button onClick={() => navigate('/login')} className="hover:text-white transition-colors text-left">Operator Login</button></li>
                <li><button onClick={() => { trackSignupStarted('footer'); navigate('/signup'); }} className="hover:text-white transition-colors text-left">Get Started Free</button></li>
              </ul>
            </div>

            {/* Support / Legal */}
            <div>
              <h5 className="text-white font-semibold mb-4">{isAr ? 'الدعم' : 'Support'}</h5>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate('/blog')} className="hover:text-white transition-colors text-left" data-testid="footer-blog-link">Blog</button></li>
                <li><button onClick={() => navigate('/support')} className="hover:text-white transition-colors text-left" data-testid="footer-help-center-link">Help Center</button></li>
                <li><a href="/support" className="hover:text-white transition-colors" data-testid="footer-contact-link">Contact Us</a></li>
                <li><button onClick={() => navigate('/privacy')} className="hover:text-white transition-colors text-left">Privacy Policy</button></li>
                <li><button onClick={() => navigate('/terms')} className="hover:text-white transition-colors text-left">Terms of Service</button></li>
                <li><button onClick={() => navigate('/delete-account')} data-testid="footer-delete-account-link" className="hover:text-white transition-colors text-left">Delete Account</button></li>
                <li><button onClick={() => navigate('/cookies')} className="hover:text-white transition-colors text-left" data-testid="footer-cookies-link">Cookie Policy</button></li>
                <li><button onClick={() => navigate('/dpa')} className="hover:text-white transition-colors text-left" data-testid="footer-dpa-link">Data Processing (DPA)</button></li>
                <li><button onClick={() => navigate('/acceptable-use')} className="hover:text-white transition-colors text-left" data-testid="footer-aup-link">Acceptable Use</button></li>
                <li><button onClick={() => navigate('/security-policy')} className="hover:text-white transition-colors text-left" data-testid="footer-security-policy-link">Security Policy</button></li>
                <li><button onClick={() => navigate('/vulnerability-disclosure')} className="hover:text-white transition-colors text-left" data-testid="footer-vdp-link">Vulnerability Disclosure</button></li>
                <li><button onClick={() => navigate('/dmca')} className="hover:text-white transition-colors text-left" data-testid="footer-dmca-link">DMCA</button></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm">
            <p>&copy; {new Date().getFullYear()} BookingFlows. All rights reserved.</p>
            <p className="text-gray-500">{isAr ? 'صُنع بواسطة مشغّلين · لأجل المشغّلين' : 'Built by operators · For operators'}</p>
          </div>
          <p className="mt-4 text-xs text-gray-600 text-center sm:text-left">
            Intuit and QuickBooks are registered trademarks of Intuit Inc. Used with permission.
          </p>
        </div>
      </footer>

      {/* Floating "Start free" CTA — appears only after the user scrolls past
          the hero. Bottom-right, mobile-safe (honors iOS bottom inset). Pure
          conversion nudge — no chat, no socials, no bot to monitor. */}
      <div
        className={`fixed right-4 sm:right-6 z-40 transition-all duration-300 ease-out pointer-events-none ${
          showFloatingCta ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}
        style={{ bottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))' }}
      >
        <button
          onClick={() => { trackSignupStarted('floating_cta'); navigate('/signup'); }}
          className="pointer-events-auto group inline-flex items-center gap-2 pl-5 pr-4 py-3 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold shadow-xl shadow-blue-500/30 transition-all hover:-translate-y-0.5"
          data-testid="supplier-floating-cta"
          aria-label="Start free"
        >
          <Sparkles className="w-4 h-4" />
          <span>{isAr ? 'ابدأ مجاناً' : 'Start free'}</span>
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
        </button>
      </div>
    </div>
  );
};

export default SupplierLandingPage;
